import React, { useState, useRef, useEffect } from 'react';
import { Button } from './button';
import { Upload, Image as ImageIcon, X } from 'lucide-react';
import { cn } from '../../lib/utils';

interface ImageUploadProps {
    onImageChange: (file: File | null) => void;
    currentImage?: string | null;
    className?: string;
    previewClassName?: string;
    label?: string;
}

export const ImageUpload = ({
    onImageChange,
    currentImage = null,
    className = '',
    previewClassName = 'w-32 h-32',
    label = "Upload image"
}: ImageUploadProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    // Helper function to format image URL
    const formatImageUrl = (imagePath: string | null | undefined): string => {
        if (!imagePath || typeof imagePath !== 'string') return '';
        if (imagePath.startsWith('http')) return imagePath;
        // Assuming the API_BASE_URL logic is handled elsewhere or passed in, 
        // but for now we'll just return the path if it's relative, 
        // or rely on the parent to pass a full URL if needed.
        // However, the original component imported getImageUrl. 
        // To keep this component pure, we might expect currentImage to be a full URL 
        // or handle it in the parent. 
        // For this refactor, let's assume currentImage is the full URL or a valid src.
        return imagePath;
    };

    useEffect(() => {
        if (currentImage) {
            setPreviewUrl(formatImageUrl(currentImage));
        } else {
            setPreviewUrl(null);
        }
    }, [currentImage]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setPreviewUrl(reader.result as string);
            };
            reader.readAsDataURL(file);
            onImageChange(file);
        }
    };

    const handleRemove = (e: React.MouseEvent) => {
        e.stopPropagation();
        setPreviewUrl(null);
        onImageChange(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className={cn("flex flex-col items-center gap-4", className)}>
            <div
                className={cn(
                    "relative group cursor-pointer border-2 border-dashed border-muted-foreground/25 hover:border-primary/50 rounded-xl flex flex-col items-center justify-center bg-muted/5 hover:bg-muted/10 transition-all duration-200 overflow-hidden",
                    previewClassName
                )}
                onClick={() => fileInputRef.current?.click()}
            >
                {previewUrl ? (
                    <>
                        <img
                            src={previewUrl}
                            alt="Preview"
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <Upload className="h-6 w-6 text-white" />
                        </div>
                        <Button
                            variant="destructive"
                            size="icon"
                            className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                            onClick={handleRemove}
                        >
                            <X className="h-3 w-3" />
                        </Button>
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center text-muted-foreground p-4 text-center">
                        <div className="p-3 bg-background rounded-full shadow-sm mb-2 group-hover:scale-110 transition-transform duration-200">
                            <ImageIcon className="h-6 w-6 text-primary/60" />
                        </div>
                        <span className="text-xs font-medium">{label}</span>
                    </div>
                )}
            </div>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
            />
        </div>
    );
};
